/** Deutsche Feiertage — clientseitig berechnet, keine API-Abhaengigkeit.
 *  Enthaelt bundesweite Feiertage + Baden-Wuerttemberg (Default fuer werkXpartner).
 *  Bei Bedarf kann man state="BY"|"NRW"|... uebergeben fuer andere Bundeslaender.
 */

type HolidayDef = { month: number; day: number; name: string }
type EasterDef = { offset: number; name: string }  // Tage relativ zu Ostersonntag

// Fixe Daten — bundesweit gueltig
const NATIONAL_FIXED: HolidayDef[] = [
  { month: 1,  day: 1,  name: "Neujahr" },
  { month: 5,  day: 1,  name: "Tag der Arbeit" },
  { month: 10, day: 3,  name: "Tag der Deutschen Einheit" },
  { month: 12, day: 25, name: "1. Weihnachtstag" },
  { month: 12, day: 26, name: "2. Weihnachtstag" },
]

// Easter-basierte — bundesweit
const NATIONAL_EASTER: EasterDef[] = [
  { offset: -2, name: "Karfreitag" },
  { offset: 1,  name: "Ostermontag" },
  { offset: 39, name: "Christi Himmelfahrt" },
  { offset: 50, name: "Pfingstmontag" },
]

// Baden-Wuerttemberg-spezifisch (zusaetzlich zu bundesweit)
const BW_FIXED: HolidayDef[] = [
  { month: 1,  day: 6,  name: "Heilige Drei Könige" },
  { month: 11, day: 1,  name: "Allerheiligen" },
]
const BW_EASTER: EasterDef[] = [
  { offset: 60, name: "Fronleichnam" },
]

/** Ostersonntag-Berechnung (Meeus/Jones/Butcher Gregorianischer Algorithmus). */
function easterDate(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return new Date(year, month - 1, day)
}

function toLocalDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export type Holiday = { date: string; name: string }

/** Feiertage fuer ein Jahr. state="BW" fuegt Baden-Wuerttemberg-spezifische hinzu. */
export function getHolidays(year: number, state: string = "BW"): Holiday[] {
  const easter = easterDate(year)
  const result: Holiday[] = []

  for (const h of NATIONAL_FIXED) {
    result.push({ date: toLocalDateStr(new Date(year, h.month - 1, h.day)), name: h.name })
  }
  for (const e of NATIONAL_EASTER) {
    const d = new Date(easter); d.setDate(d.getDate() + e.offset)
    result.push({ date: toLocalDateStr(d), name: e.name })
  }

  if (state === "BW") {
    for (const h of BW_FIXED) {
      result.push({ date: toLocalDateStr(new Date(year, h.month - 1, h.day)), name: h.name })
    }
    for (const e of BW_EASTER) {
      const d = new Date(easter); d.setDate(d.getDate() + e.offset)
      result.push({ date: toLocalDateStr(d), name: e.name })
    }
  }

  return result.sort((a, b) => a.date.localeCompare(b.date))
}

/** Feiertage im Zeitraum (fuer CalendarView-Range). */
export function getHolidaysInRange(from: Date, to: Date, state: string = "BW"): Holiday[] {
  const years = new Set<number>()
  for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
    years.add(d.getFullYear())
  }
  const fromStr = toLocalDateStr(from)
  const toStr = toLocalDateStr(to)
  const all: Holiday[] = []
  for (const y of years) {
    all.push(...getHolidays(y, state))
  }
  return all.filter((h) => h.date >= fromStr && h.date <= toStr)
}
